import React, { useMemo } from 'react';
import { Box, FieldTypes, FlexForm, Inscription, Preloader } from '@common-fe/common-fe';

import ROUTES from '@/common/routes';
import { PaymentMethod } from '@/modules/employee/employee.types';
import { PersonalInformationPayload } from '@/modules/employee/Employee/PersonalInformation/hooks/usePersonalInformationState';
import { PaymentMethodVerificationStatus } from '@/modules/ReimburseMe/types/payment.types';

import PaymentMethodField from './PaymentMethodField';

export interface DefaultPaymentMethodBlockProps {
  isLoading: boolean;
  activeMethod: PaymentMethod | undefined;
  paymentEmployeeFee: string | undefined;
  isVerificationFailed: boolean;
  isVerificationPending: boolean;
}

interface Props {
  isLoading?: boolean;
  isBankAccountLoading?: boolean;
  isRefund?: boolean;
  employee?: Partial<PersonalInformationPayload> | null;
  paymentMethod?: PaymentMethod;
  paymentEmployeeFee?: string;
  renderWrapper?: (props: DefaultPaymentMethodBlockProps) => React.ReactNode;
}

const ImmediatelyButton: React.FC<Props> = ({
  isLoading,
  isBankAccountLoading,
  isRefund,
  employee,
  paymentMethod,
  paymentEmployeeFee,
  renderWrapper,
}) => {
  const isVerificationFailed = useMemo(() =>
    paymentMethod?.verificationStatus === PaymentMethodVerificationStatus.VERIFICATION_FAILED ||
    paymentMethod?.verificationStatus === PaymentMethodVerificationStatus.EXPIRED,
  [paymentMethod]);

  const isVerificationPending = useMemo(() =>
    paymentMethod?.verificationStatus === PaymentMethodVerificationStatus.PENDING ||
    paymentMethod?.verificationStatus === PaymentMethodVerificationStatus.PENDING_MANUAL,
  [paymentMethod]);

  if (renderWrapper) {
    return (
      <>
        {renderWrapper({
          isLoading: Boolean(isLoading),
          activeMethod: paymentMethod || undefined,
          paymentEmployeeFee,
          isVerificationFailed,
          isVerificationPending,
        })}
      </>
    );
  }

  return (
    <>
      {isLoading || isBankAccountLoading ? (
        <Box pad={{ vertical: 'spacing24' }}>
          <Preloader testId="payment-method-loader" />
        </Box>
      ) : (
        <Box data-testId="payment-method-card" pad="spacing24">
          {isRefund ? null : (
            <Box direction="row" margin={{ bottom: 'spacing24' }}>
              <Inscription
                cursor="pointer"
                title={employee?.fullName}
                size="xxlarge"
                color="textAccent"
                lineHeight="28px"
                onClick={() => window.open(ROUTES.EMPLOYEE_VIEW(employee?.internalId || ''), '_blank')}
              >
                {`${employee?.firstName || ''} ${employee?.lastName || ''}`.trim()}
              </Inscription>
              <Inscription color="textBody" size="xxlarge" lineHeight="28px">
                's payment will be sent to
              </Inscription>
            </Box>
          )}
          <Box>
            <FlexForm
              contentStyles={{ margin: 0 }}
              paddingless
              fields={[
                {
                  name: 'paymentMethod',
                  type: FieldTypes.Node,
                  label: (
                    <Box height={{ min: '40px' }} justify="center">
                      {isRefund ? 'Refund method' : 'Payment method'}
                    </Box>
                  ),
                  // @ts-ignore
                  isThinMode: true,
                  placeholder: 'Payment method',
                  value: (
                    <PaymentMethodField
                      paymentMethod={paymentMethod}
                      paymentEmployeeFee={paymentEmployeeFee}
                    />
                  ),
                  ...isRefund ? { controlStyles: { width: '500px' } } : {},
                },
              ]}
              isThinMode
            />
          </Box>
        </Box>
      )}
    </>
  );
};

export default ImmediatelyButton;
